import Vue from 'vue'
import Vuex from 'vuex'
import { i18n } from '../lang'
import student from './modules/student'
import poi from './modules/poi'
import explore from './modules/explore'
import curated from './modules/curated'
import personalized from './modules/personalized'
import history from './modules/history'

Vue.use(Vuex)

function calculateDistance (lat1, lon1, lat2, lon2) {
  let R = 3959 // miles
  let φ1 = lat1 * Math.PI / 180
  let φ2 = lat2 * Math.PI / 180
  let Δφ = (lat2 - lat1) * Math.PI / 180
  let Δλ = (lon2 - lon1) * Math.PI / 180

  let a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return (R * c)
}

const defaultLocation = {
  lat: 39.08381935642708,
  lon: -94.58427032861896
}

export default new Vuex.Store({
  modules: {
    student,
    poi,
    explore,
    curated,
    personalized,
    history
  },
  state: {
    loadCounter: 1,
    position: undefined,
    positionError: undefined,
    positionWatch: undefined,
    useDefaultLocation: false,
    showDefaultLocationNotification: true,
    college: {
      id: 1, // used as a default value for development
      languages: [],
      settings: undefined,
      namecaptureToken: 'cad72bde-9438-11e9-85de-be42d1ced817',
      name: 'Boston University'
    },
    locale: 'en',
    loadedLanguages: ['']
  },
  getters: {
    getPOIDistance: (state) => (poi) => {
      if (state.useDefaultLocation) {
        if (state.college.lat && state.college.lon) {
          return calculateDistance(
            state.college.lat,
            state.college.lon,
            poi.lat,
            poi.lon
          )
        } else {
          return calculateDistance(
            defaultLocation.lat,
            defaultLocation.lon,
            poi.lat,
            poi.lon
          )
        }
      } else if (poi.distance) {
        return poi.distance
      } else if (state.position && state.position.coords) {
        return calculateDistance(
          state.position.coords.latitude,
          state.position.coords.longitude,
          poi.lat,
          poi.lon
        )
      } else {
        return undefined
      }
    },
    getUserDistance: (state) => {
      if (state.position && state.position.coords) {
        return calculateDistance(
          state.position.coords.latitude,
          state.position.coords.longitude,
          state.college.lat,
          state.college.lon
        )
      } else {
        return undefined
      }
    },
    getUserPosition: (state) => {
      if (state.useDefaultLocation) {
        if (state.college.lat && state.college.lon) {
          return {
            lat: state.college.lat,
            lon: state.college.lon
          }
        } else {
          return {
            lat: defaultLocation.lat,
            lon: defaultLocation.lon
          }
        }
      } else if (state.position && state.position.coords) {
        return {
          lat: state.position.coords.latitude,
          lon: state.position.coords.longitude
        }
      } else {
        return undefined
      }
    }
  },
  mutations: {
    startLoad (state) {
      state.loadCounter += 1
    },
    endLoad (state) {
      state.loadCounter -= 1
    },
    setPosition (state, position) {
      state.position = position
    },
    setPositionError (state, error) {
      state.useDefaultLocation = true
      state.positionError = error
    },
    setPositionWatch (state, watch) {
      state.positionWatch = watch
    },
    setCollege (state, college) {
      state.college = college
    },
    setLanguage (state, language) {
      if (!state.loadedLanguages.includes(language)) {
        import(/* webpackChunkName: "lang-[request]" */ `@/languages/${language}.js`).then(
          messages => {
            i18n.setLocaleMessage(language, messages.default)
            state.loadedLanguages.push(language)
          }
        )
      }
      state.locale = language
      i18n.locale = language
      localStorage.locale = language
    },
    useDefaultLocation (state, value) {
      state.useDefaultLocation = value
    },
    showDefaultLocationNotification (state, value) {
      state.showDefaultLocationNotification = value
    }
  },
  actions: {
    watchPosition ({ state, commit, getters }) {
      // only update position once every 30s
      const MIN_LOCATION_UPDATE_TIME = 30000

      let initialized = false
      commit('startLoad')

      let positionUpdates = []
      let isDebouncing = false

      // assumes nothing else is updating state.position, and that there is only one state.watchPosition
      // that has been called.
      let watch = navigator.geolocation.watchPosition((position) => {
        if (!initialized) {
          initialized = true
          commit('endLoad')
        }
        let positionRecentlyUpdated = state.position && (position.timestamp - state.position.timestamp < MIN_LOCATION_UPDATE_TIME)
        if (!state.position || !positionRecentlyUpdated) {
          commit('setPosition', position)

          let userDistance = getters.getUserDistance
          if (userDistance === undefined || userDistance > 10.0) {
            commit('useDefaultLocation', true)
          } else {
            commit('useDefaultLocation', false)
          }
        } else {
          // debounce position updates.
          // apply the most recently received location update at most MIN_LOCATION_UPDATE_TIME
          positionUpdates.push(position)

          if (!isDebouncing) {
            isDebouncing = true
            setTimeout(() => {
              isDebouncing = false
              let p = positionUpdates.pop()
              positionUpdates = []
              commit('setPosition', p)

              let userDistance = getters.getUserDistance
              if (userDistance === undefined || userDistance > 10.0) {
                commit('useDefaultLocation', true)
              } else {
                commit('useDefaultLocation', false)
              }
            }, MIN_LOCATION_UPDATE_TIME - (position.timestamp - state.position.timestamp))
          }
        }

        if (state.positionError) {
          commit('setPositionError', undefined)
        }
      }, (error) => {
        if (!initialized) {
          initialized = true
          commit('endLoad')
        }

        // Checking for a previous error to prevent spamming
        if (state.positionError === undefined) {
          // Making a copy of PositionError as it's impossible to store it as is
          let errorObj = {}

          for (let key in error) {
            errorObj[key] = error[key]
          }

          commit('setPositionError', errorObj)
        }
      }, {
        enableHighAccuracy: true,
        maximumAge: 60000
      })

      commit('setPositionWatch', watch)
    },
    unwatchPosition ({ state, commit }) {
      navigator.geolocation.clearWatch(state.positionWatch)

      commit('setPositionWatch', undefined)
    },
    getLanguage ({ commit }) {
      if (localStorage.locale) {
        commit('setLanguage', localStorage.locale)
      } else {
        commit('setLanguage', 'en')
      }
    },
    changeLanguage ({ commit }, language) {
      commit('setLanguage', language)
    }
  }
})

Vue.prototype.$focusOutsidePopup = function () {
  const focusElem = document.querySelector(
    `main [tabindex="0"]:not([style="display:none"])
    main input:not([style="display:none"]),
    main button:not([style="display:none"]),
    main a:not([style="display:none"])`)
  if (focusElem) {
    focusElem.focus()
  }
}
Vue.prototype.$focusInsidePopup = function () {
  const focusElem = document.querySelector(
    `[role="dialog"] [tabindex="0"]:not([style="display:none"])
    [role="dialog"] input:not([style="display:none"]),
    [role="dialog"] button:not([style="display:none"]),
    [role="dialog"] a:not([style="display:none"])`)
  if (focusElem) {
    focusElem.focus()
  }
}
